import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ImgHTMLAttributes,
  type ReactNode,
} from 'react';
import { focalPointToImageTransform } from '@/utils/focalPoint';
import styles from './FocalImage.module.css';

interface NaturalImageSize {
  width: number;
  height: number;
}

const naturalSizeCache = new Map<string, NaturalImageSize>();
const NATURAL_SIZE_CACHE_LIMIT = 200;

function rememberNaturalSize(src: string, size: NaturalImageSize) {
  if (!naturalSizeCache.has(src) && naturalSizeCache.size >= NATURAL_SIZE_CACHE_LIMIT) {
    const oldestSrc = naturalSizeCache.keys().next().value;
    if (oldestSrc) naturalSizeCache.delete(oldestSrc);
  }
  naturalSizeCache.set(src, size);
}

export interface FocalImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'children' | 'className' | 'src' | 'style'> {
  src: string;
  alt: string;
  focalPointX?: number | null;
  focalPointY?: number | null;
  focalPointScale?: number | null;
  className?: string;
  imageClassName?: string;
  style?: CSSProperties;
  children?: ReactNode;
  'data-testid'?: string;
  'data-muted'?: boolean | string;
  'data-photo'?: boolean | string;
}

export function FocalImage({
  src,
  alt,
  focalPointX = null,
  focalPointY = null,
  focalPointScale = null,
  className,
  imageClassName,
  style,
  children,
  onLoad,
  'data-testid': testId,
  'data-muted': dataMuted,
  'data-photo': dataPhoto,
  ...imageProps
}: FocalImageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState<NaturalImageSize | null>(
    () => naturalSizeCache.get(src) ?? null,
  );

  const measure = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    setContainerSize((current) =>
      current.width === rect.width && current.height === rect.height
        ? current
        : { width: rect.width, height: rect.height },
    );
  }, []);

  useLayoutEffect(() => {
    setNaturalSize(naturalSizeCache.get(src) ?? null);
  }, [src]);

  useLayoutEffect(() => {
    measure();
    const root = rootRef.current;
    if (!root) return;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(root);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const transform = naturalSize
    ? focalPointToImageTransform(
        {
          containerWidth: containerSize.width,
          containerHeight: containerSize.height,
          naturalWidth: naturalSize.width,
          naturalHeight: naturalSize.height,
        },
        focalPointX,
        focalPointY,
        focalPointScale,
      )
    : null;

  return (
    <div
      ref={rootRef}
      className={`${styles.root}${className ? ` ${className}` : ''}`}
      style={style}
      data-testid={testId}
      data-muted={dataMuted}
      data-photo={dataPhoto}
    >
      <img
        {...imageProps}
        src={src}
        alt={alt}
        className={`${styles.image}${imageClassName ? ` ${imageClassName}` : ''}`}
        width={naturalSize?.width}
        height={naturalSize?.height}
        style={{
          width: naturalSize?.width,
          height: naturalSize?.height,
          opacity: transform?.scale ? 1 : 0,
          transform: transform?.scale
            ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`
            : undefined,
        }}
        onLoad={(event) => {
          const size = {
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          };
          if (size.width > 0 && size.height > 0) {
            rememberNaturalSize(src, size);
            setNaturalSize(size);
          }
          onLoad?.(event);
        }}
        draggable={false}
      />
      {children}
    </div>
  );
}
